/**
 * Simplified-landing card contracts (design/handoff_simplified_landing §3).
 *
 * Cards are produced by entity-skill TOOLS (never by the LLM) and attached to the
 * assistant message as `{ type: 'card', card }` parts. Every action/chip is a
 * MESSAGE the user can say back into the conversation — cards never call APIs.
 */
import { z } from 'zod';
import { RUNTIME_POLICY_SURFACES } from '../model-runtime-policy.js';
import { AccountPersonaDisclosureSchema } from '../account-persona/disclosure.js';

// ─── Shared fragments ────────────────────────────────────────────────────────

const CardBase = {
  /** Stable per-message id (used for card_update patches). */
  id: z.string().min(1).max(120),
  /** Owning module ('core' | 'finance' | …). */
  moduleId: z.string().min(1).max(50),
  /** The card is about, or from, an AI simulation of a real account (account-persona 04): the client shows a badge. */
  disclosure: AccountPersonaDisclosureSchema.optional(),
};

export const CardToneSchema = z.enum(['ok', 'warn', 'bad', 'accent', 'neutral']);
export type CardTone = z.infer<typeof CardToneSchema>;

export const CardActionSchema = z.object({
  label: z.string().min(1).max(80),
  /** The message sent into the conversation when the user presses the action. */
  message: z.string().min(1).max(500),
  primary: z.boolean().optional(),
  /** The action proposes a lifecycle/capital change and will need a second approver. */
  requiresApproval: z.boolean().optional(),
});
export type CardAction = z.infer<typeof CardActionSchema>;

export const CardSuggestionSchema = z.object({
  label: z.string().min(1).max(80),
  message: z.string().min(1).max(500),
});
export type CardSuggestion = z.infer<typeof CardSuggestionSchema>;

/** At most this many replies a member offers on one answer (`conversation_suggest_replies`). */
export const MAX_SUGGESTED_REPLIES = 4;

/** The input of `conversation_suggest_replies`: the replies a member offers the person, each sent as written. */
export const SuggestedRepliesInputSchema = z.object({
  replies: z.array(CardSuggestionSchema).min(1).max(MAX_SUGGESTED_REPLIES),
});

const LabelValueSchema = z.object({
  label: z.string().min(1).max(80),
  value: z.string().max(500),
  tone: CardToneSchema.optional(),
});

// ─── 3.1 status_summary ──────────────────────────────────────────────────────

export const StatusSummaryCardSchema = z.object({
  ...CardBase,
  type: z.literal('status_summary'),
  asOf: z.string().min(1),
  stats: z
    .array(
      z.object({
        key: z.string().min(1).max(60),
        value: z.union([z.number(), z.string()]),
        label: z.string().min(1).max(80),
        sub: z.string().max(160).optional(),
        tone: CardToneSchema.optional(),
      }),
    )
    .min(1)
    .max(8),
  suggestions: z.array(CardSuggestionSchema).max(6).optional(),
});

// ─── Generic entity cards (core skills) ──────────────────────────────────────

export const EntityListCardSchema = z.object({
  ...CardBase,
  type: z.literal('entity_list'),
  entity: z.string().min(1).max(60),
  title: z.string().min(1).max(120),
  total: z.number().int().nonnegative(),
  items: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        title: z.string().min(1).max(200),
        subtitle: z.string().max(240).optional(),
        status: z.object({ label: z.string().min(1).max(40), tone: CardToneSchema.optional() }).optional(),
        meta: z.array(LabelValueSchema).max(6).optional(),
        /** Message sent when the row is pressed (e.g. "Dive into <name>"). */
        message: z.string().max(500).optional(),
      }),
    )
    .max(50),
  actions: z.array(CardActionSchema).max(4).optional(),
});

export const EntityDetailCardSchema = z.object({
  ...CardBase,
  type: z.literal('entity_detail'),
  entity: z.string().min(1).max(60),
  entityId: z.union([z.string(), z.number()]),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(240).optional(),
  status: z.object({ label: z.string().min(1).max(40), tone: CardToneSchema.optional() }).optional(),
  fields: z.array(LabelValueSchema).max(24),
  sections: z
    .array(z.object({ title: z.string().min(1).max(80), items: z.array(LabelValueSchema).max(24) }))
    .max(8)
    .optional(),
  recommendation: z.object({ text: z.string().min(1), reason: z.string().min(1) }).optional(),
  actions: z.array(CardActionSchema).max(4).optional(),
});

// ─── 3.2 strategy_list / 3.3 strategy_detail (finance) ───────────────────────

export const StrategyStageSchema = z.enum(['draft', 'backtested', 'paper', 'live', 'evaluating']);
export type StrategyStage = z.infer<typeof StrategyStageSchema>;

export const StrategyListCardSchema = z.object({
  ...CardBase,
  type: z.literal('strategy_list'),
  total: z.number().int().nonnegative(),
  items: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        name: z.string().min(1).max(200),
        asset: z.string().max(80),
        timeframe: z.string().max(20),
        owner: z.string().max(120),
        stage: StrategyStageSchema,
        sharpe: z.number().nullable().optional(),
        maxDrawdownPct: z.number().nullable().optional(),
        attention: z.enum(['blocked', 'approval']).nullable().optional(),
        /** Message sent when the row is pressed. */
        message: z.string().max(500).optional(),
      }),
    )
    .max(100),
  suggestions: z.array(CardSuggestionSchema).max(6).optional(),
});

export const StrategyMetricsSchema = z.object({
  sharpe: z.number().nullable(),
  maxDrawdownPct: z.number().nullable(),
  winRatePct: z.number().nullable(),
  profitFactor: z.number().nullable(),
  cagrPct: z.number().nullable(),
  trades: z.number().int().nullable(),
  since: z.string().nullable(),
});

export const StrategyDetailCardSchema = z.object({
  ...CardBase,
  type: z.literal('strategy_detail'),
  strategyId: z.union([z.string(), z.number()]),
  name: z.string().min(1).max(200),
  asset: z.string().max(80),
  timeframe: z.string().max(20),
  stage: StrategyStageSchema,
  summary: z.string().max(2000),
  verdict: z.enum(['pass', 'needs_work', 'blocked']),
  metrics: StrategyMetricsSchema,
  /** Same length as benchmarkCurve when both present. */
  equityCurve: z.array(z.number()).max(2000).optional(),
  benchmarkCurve: z.array(z.number()).max(2000).optional(),
  recommendation: z.object({ text: z.string().min(1), reason: z.string().min(1) }),
  actions: z.array(CardActionSchema).min(1).max(4),
});

// ─── 3.4 work_progress ───────────────────────────────────────────────────────

export const WorkProgressStepStateSchema = z.enum(['pending', 'running', 'done', 'failed']);

export const WorkProgressCardSchema = z.object({
  ...CardBase,
  type: z.literal('work_progress'),
  title: z.string().min(1).max(160),
  steps: z
    .array(
      z.object({
        label: z.string().min(1).max(160),
        owner: z.string().max(80).optional(),
        state: WorkProgressStepStateSchema,
      }),
    )
    .min(1)
    .max(12),
  runIds: z.array(z.union([z.string(), z.number()])).max(20),
});

// ─── 3.5 approval_request ────────────────────────────────────────────────────

export const ApprovalDecisionRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('daemon_decision'), decisionId: z.number().int().positive() }),
  z.object({ kind: z.literal('finance_artifact'), artifactId: z.number().int().positive() }),
]);
export type ApprovalDecisionRef = z.infer<typeof ApprovalDecisionRefSchema>;

/**
 * The runtime policy proposed work will run under (owner 2026-10-14: policies are part of every proposal, with a clear
 * indication). `changeable`: what approval applies carries it (a hire, a workflow), so it can be changed before then;
 * otherwise it is the surface's policy the work will run under, shown for what it is.
 */
export const CardRuntimePolicySchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1).max(200),
  surface: z.enum(RUNTIME_POLICY_SURFACES),
  changeable: z.boolean(),
});
export type CardRuntimePolicy = z.infer<typeof CardRuntimePolicySchema>;

export const ApprovalRequestCardSchema = z.object({
  ...CardBase,
  type: z.literal('approval_request'),
  decisionRef: ApprovalDecisionRefSchema,
  title: z.string().min(1).max(160),
  action: z.string().min(1).max(500),
  proposedBy: z.object({ userId: z.number().int().nullable(), via: z.string().min(1).max(80) }),
  requiredPolicy: z.string().min(1).max(80),
  eligibleApprovers: z
    .array(z.object({ userId: z.number().int(), name: z.string().max(120), department: z.string().max(80).optional() }))
    .max(20),
  /**
   * The risk check made when the request was raised. `passed: null` is "not recorded": a card rebuilt from a stored
   * request that kept no check (an employee asking to run, a daemon's finding) says so, instead of claiming one failed.
   */
  riskCheck: z.object({ passed: z.boolean().nullable(), notes: z.string().max(1000) }),
  explanation: z.string().max(2000),
  state: z.enum(['draft', 'pending', 'approved', 'rejected', 'cancelled']),
  actions: z.array(CardActionSchema).max(4).optional(),
  runtimePolicy: CardRuntimePolicySchema.optional(),
});

// ─── 3.6 clarification ───────────────────────────────────────────────────────

export const ClarificationCardSchema = z.object({
  ...CardBase,
  type: z.literal('clarification'),
  question: z.string().min(1).max(500),
  options: z.array(CardSuggestionSchema).min(1).max(5),
  allowFreeText: z.literal(true),
});

// ─── 3.7 comparison ──────────────────────────────────────────────────────────

export const ComparisonCardSchema = z.object({
  ...CardBase,
  type: z.literal('comparison'),
  window: z.object({ from: z.string(), to: z.string() }),
  columns: z
    .array(z.object({ strategyId: z.union([z.string(), z.number()]), name: z.string().min(1) }))
    .min(2)
    .max(6),
  rows: z
    .array(z.object({ metric: z.string().min(1).max(80), values: z.array(z.union([z.number(), z.string()])) }))
    .max(20),
  read: z.string().max(2000),
  actions: z.array(CardActionSchema).max(4).optional(),
});

// ─── 3.8 proactive_prompt ────────────────────────────────────────────────────

export const ProactivePromptCardSchema = z.object({
  ...CardBase,
  type: z.literal('proactive_prompt'),
  context: z.object({
    entity: z.string().min(1).max(60),
    id: z.union([z.string(), z.number()]).optional(),
  }),
  /** One sentence; offers, never orders. */
  text: z.string().min(1).max(300),
  options: z
    .array(
      CardSuggestionSchema.extend({
        skill: z.string().max(80).optional(),
        requiresApproval: z.boolean().optional(),
      }),
    )
    .min(1)
    .max(5),
  dismissible: z.literal(true),
});

// ─── 3.9 connection_setup ────────────────────────────────────────────────────

export const ConnectionSetupCardSchema = z.object({
  ...CardBase,
  type: z.literal('connection_setup'),
  provider: z.string().min(1).max(60),
  transport: z.enum(['mcp', 'oauth', 'apikey']),
  scopes: z.array(z.string().max(120)).max(20),
  usedFor: z.string().max(300),
  state: z.enum(['proposed', 'authorized', 'failed']),
  actions: z.array(CardActionSchema).max(4).optional(),
});

// ─── Union ───────────────────────────────────────────────────────────────────

export const CardSchema = z.discriminatedUnion('type', [
  StatusSummaryCardSchema,
  EntityListCardSchema,
  EntityDetailCardSchema,
  StrategyListCardSchema,
  StrategyDetailCardSchema,
  WorkProgressCardSchema,
  ApprovalRequestCardSchema,
  ClarificationCardSchema,
  ComparisonCardSchema,
  ProactivePromptCardSchema,
  ConnectionSetupCardSchema,
]);

export type Card = z.infer<typeof CardSchema>;
export type CardType = Card['type'];
export const CARD_TYPES = CardSchema.options.map((o) => o.shape.type.value) as CardType[];

/** Card types whose presence means the turn is waiting on the user (sets conversations.needs_user). */
export const NEEDS_USER_CARD_TYPES: ReadonlySet<CardType> = new Set<CardType>(['clarification', 'approval_request']);

/** Card types that show one record: a turn that ends on one has that record as its outcome. */
export const RECORD_CARD_TYPES: ReadonlySet<CardType> = new Set<CardType>(['entity_detail', 'strategy_detail']);

/**
 * Card types a list or search tool returns. When a turn's outcome is something else (it asks the person, or shows a
 * record), such a card was the agent looking around, and it goes behind the scenes as one line instead of a card
 * (owner 2026-10-31: a member asked to help create a strategy showed every strategy, which nobody asked for).
 */
export const LOOKUP_CARD_TYPES: ReadonlySet<CardType> = new Set<CardType>(['entity_list', 'strategy_list']);

/** The behind-the-scenes line a lookup card becomes when it is not the turn's answer. */
export function lookupSummaryOf(card: Card): string | null {
  if (card.type === 'entity_list') return `Looked up ${card.title.toLowerCase()} (${card.total})`;
  if (card.type === 'strategy_list') return `Looked up ${card.total} ${card.total === 1 ? 'strategy' : 'strategies'}`;
  return null;
}

/**
 * Parses an arbitrary tool-output `cards` value into validated cards. Invalid
 * entries are dropped (returned in `rejected`) so one bad card never hides the rest.
 */
export function parseCards(value: unknown): { cards: Card[]; rejected: Array<{ index: number; error: string }> } {
  if (!Array.isArray(value)) return { cards: [], rejected: [] };
  const cards: Card[] = [];
  const rejected: Array<{ index: number; error: string }> = [];
  value.forEach((candidate, index) => {
    const parsed = CardSchema.safeParse(candidate);
    if (parsed.success) cards.push(parsed.data);
    else
      rejected.push({ index, error: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') });
  });
  return { cards, rejected };
}
